import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { ArrowDownUpIcon, CheckIcon } from './icons';

export interface ListSortOption {
  /** Stable identifier passed back to `onSelect` — usually `${key}-${dir}`. */
  id: string;
  label: string;
  isActive: boolean;
}

interface ListSortDropdownProps {
  options: ListSortOption[];
  onSelect: (id: string) => void;
  /**
   * When true, the trigger button takes the active "primary-tinted" treatment
   * (border-primary + bg-secondary). Pass `true` whenever the current sort
   * differs from the page's implicit default.
   */
  isActive?: boolean;
  /**
   * When provided, a "Remover ordenação" item is appended whenever
   * `isActive=true` so the user can fall back to the default ordering.
   */
  onClear?: () => void;
  /** Label for the trigger when no sort is active. */
  ariaLabel: string;
  /**
   * Optional richer label for the active state — e.g. "Ordenação atual:
   * Nome (A → Z)". Falls back to `ariaLabel` when no sort active.
   */
  activeAriaLabel?: string;
  /** Test id forwarded to the trigger button. */
  'data-testid'?: string;
}

/**
 * Icon-only sort trigger + dropdown of options. Each option is selectable;
 * the currently active one renders a check on the right.
 *
 * Used by list pages — the API accepts an opaque option `id`, leaving the
 * (key, dir) tuple structure to the container.
 */
export function ListSortDropdown({
  options,
  onSelect,
  isActive,
  onClear,
  ariaLabel,
  activeAriaLabel,
  ...rest
}: ListSortDropdownProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          aria-label={isActive ? (activeAriaLabel ?? ariaLabel) : ariaLabel}
          aria-pressed={isActive}
          data-state={isActive ? 'selected' : 'default'}
          data-testid={rest['data-testid']}
          className={cn(
            isActive && 'border-primary bg-secondary text-primary hover:bg-secondary/80'
          )}
        >
          <ArrowDownUpIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {options.map((option) => (
          <DropdownMenuItem
            key={option.id}
            onClick={() => onSelect(option.id)}
            aria-checked={option.isActive}
            data-testid={
              rest['data-testid'] ? `${rest['data-testid']}-option-${option.id}` : undefined
            }
            className="justify-between"
          >
            <span className={cn(option.isActive && 'font-medium text-foreground')}>
              {option.label}
            </span>
            {option.isActive ? <CheckIcon className="size-4 text-primary" /> : null}
          </DropdownMenuItem>
        ))}
        {onClear && isActive ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onClear}
              data-testid={rest['data-testid'] ? `${rest['data-testid']}-clear` : undefined}
              className="text-muted-foreground"
            >
              Remover ordenação
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
